import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { studio } from "@/data/projects";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EASE = [0.25, 0.1, 0.25, 1];

const items = [
  { label: "Email", value: studio.email, href: `mailto:${studio.email}` },
  { label: "Phone", value: studio.phone, href: `tel:${studio.phone.replace(/\s/g, "")}` },
  { label: "Location", value: studio.location, href: null },
  { label: "Instagram", value: studio.instagram, href: studio.instagramUrl },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in every field.");
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch(`${API}/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setStatus("success");
      setForm({ name: "", email: "", message: "" });
      toast.success("Thank you — your enquiry has been sent.");
    } catch (err) {
      setStatus("idle");
      toast.error("Something went wrong. Please email us directly.");
    }
  };

  return (
    <section
      id="contact"
      className="relative z-10 scroll-mt-24 border-t border-[#e4e4e2] bg-white"
      data-testid="contact-section"
    >
      <div className="mx-auto max-w-[1600px] px-6 py-28 md:px-12 md:py-40">
        <span className="ama-label text-[#1B3818]">Contact</span>

        <motion.a
          href={`mailto:${studio.email}`}
          data-cursor="link"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE }}
          className="group mt-10 block max-w-6xl text-[10vw] font-light leading-[0.95] tracking-tight md:text-[7rem]"
          data-testid="contact-cta"
        >
          <span className="transition-colors duration-500 group-hover:text-[#1B3818]">
            Let&rsquo;s build
          </span>
          <br />
          <span className="text-black/40 transition-colors duration-500 group-hover:text-black">
            something quiet.
          </span>
        </motion.a>

        <div className="mt-20 grid grid-cols-1 gap-16 md:mt-28 md:grid-cols-12">
          {/* Enquiry form */}
          <form
            onSubmit={submit}
            className="md:col-span-7"
            data-testid="enquiry-form"
            noValidate
          >
            <span className="ama-label text-black/40">Start an enquiry</span>
            <div className="mt-8 space-y-8">
              <div className="border-b border-[#d8d8d4] pb-2">
                <label className="ama-label text-black/40" htmlFor="f-name">
                  Name
                </label>
                <input
                  id="f-name"
                  data-testid="enquiry-name"
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  className="mt-3 w-full bg-transparent text-lg font-light outline-none placeholder:text-black/25"
                />
              </div>
              <div className="border-b border-[#d8d8d4] pb-2">
                <label className="ama-label text-black/40" htmlFor="f-email">
                  Email
                </label>
                <input
                  id="f-email"
                  data-testid="enquiry-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@studio.com"
                  className="mt-3 w-full bg-transparent text-lg font-light outline-none placeholder:text-black/25"
                />
              </div>
              <div className="border-b border-[#d8d8d4] pb-2">
                <label className="ama-label text-black/40" htmlFor="f-msg">
                  Message
                </label>
                <textarea
                  id="f-msg"
                  data-testid="enquiry-message"
                  rows={3}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your project"
                  className="mt-3 w-full resize-none bg-transparent text-lg font-light outline-none placeholder:text-black/25"
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="enquiry-submit"
              data-cursor="link"
              disabled={status === "sending"}
              className="group mt-10 inline-flex items-center gap-4 border border-black px-8 py-4 ama-label transition-colors duration-500 hover:border-[#1B3818] hover:bg-[#1B3818] hover:text-white disabled:opacity-40"
            >
              {status === "sending"
                ? "Sending"
                : status === "success"
                ? "Sent"
                : "Send enquiry"}
              <span className="h-1.5 w-1.5 rounded-full bg-[#1B3818] transition-colors duration-500 group-hover:bg-white" />
            </button>
          </form>

          {/* Details */}
          <div className="md:col-span-4 md:col-start-9">
            <span className="ama-label text-black/40">Studio</span>
            <div className="mt-8 divide-y divide-[#e4e4e2] border-y border-[#e4e4e2]">
              {items.map((it) => {
                const inner = (
                  <div className="flex items-baseline justify-between py-5">
                    <span className="ama-label text-black/40">{it.label}</span>
                    <span className="text-base font-light transition-colors duration-500 group-hover:text-[#1B3818]">
                      {it.value}
                    </span>
                  </div>
                );
                return it.href ? (
                  <a
                    key={it.label}
                    href={it.href}
                    target={it.label === "Instagram" ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    data-cursor="link"
                    data-testid={`contact-${it.label.toLowerCase()}`}
                    className="group block"
                  >
                    {inner}
                  </a>
                ) : (
                  <div
                    key={it.label}
                    data-testid={`contact-${it.label.toLowerCase()}`}
                    className="group"
                  >
                    {inner}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[#e4e4e2]">
        <div className="mx-auto flex max-w-[1600px] flex-col items-start justify-between gap-4 px-6 py-10 md:flex-row md:items-center md:px-12">
          <span className="text-sm font-medium tracking-[0.3em]">AMA</span>
          <span className="ama-label text-black/40">
            Ante Meridian Architects — {new Date().getFullYear()}
          </span>
          <span className="ama-label text-black/40">Dubai, United Arab Emirates</span>
        </div>
      </footer>
    </section>
  );
}
